import { Injectable } from '@angular/core';
import { ItemEntityExtendedType } from '@models/item-entity-extended.type';
import { FILTER_TYPE, IFilter } from '@shared/components/filters-dialog/interfaces/filters.interface';
import { ATTRIBUTES } from '@shared/constants/attributes.constant';
import { CHECKBOX_STATE } from '@shared/constants/checkbox-states.constant';
import { getItemNameBySymbol } from '@shared/constants/game.constant';
import { countItems, isOtherItem, resolveValueByPath } from '@shared/utils';

@Injectable({
  providedIn: 'root',
})
export class SortItemsService {
  sortItems(
    originalItems: ItemEntityExtendedType[],
    currentFilterQuery: string | null,
    originalFilters?: IFilter[],
    generalFilters?: Record<string, { min: string; max: string }>,
    onlyConcreteItemType = -1,
    isCollapseItems = false,
    sortDirection = -1,
    currentPositionSort?: string,
    showMyItems = false,
  ) {
    let items: ItemEntityExtendedType[] = originalItems;

    if (showMyItems) {
      items = items.filter(item => item.isOwner);
    }

    if (!!currentPositionSort) {
      const itemProp = currentPositionSort;

      items = items.sort((item1: ItemEntityExtendedType, item2: ItemEntityExtendedType) => {
        return sortDirection === -1
          ? (item2[itemProp] as number) - (item1[itemProp] as number)
          : (item1[itemProp] as number) - (item2[itemProp] as number);
      });
    }

    if (!!currentFilterQuery) {
      items = items.filter(
        item =>
          getItemNameBySymbol(item.meta.symbol)
            .toLowerCase()
            .indexOf(currentFilterQuery.toLowerCase() || '') >= 0,
      );
    }

    if (originalFilters?.length) {
      const filters = originalFilters ?? [];
      items = items.filter(item => {
        if (filters.length === 0) {
          return true;
        }

        let result = true;
        let atLeastOneOrExist = true;
        // if we have at least one OR filter atLeastOneOrExist should be set on true during search
        if (filters.filter(f => f.and !== CHECKBOX_STATE.CHECKED).length > 0) {
          atLeastOneOrExist = false;
        }

        for (const f of filters) {
          // if at least one OR success no sense check other
          if ((f.and !== CHECKBOX_STATE.CHECKED && atLeastOneOrExist) || !f.attribute) {
            continue;
          }

          let exist = false;
          if (f.filterType === FILTER_TYPE.COMMON) {
            exist = item.attributes[f.attribute.id] >= +f.value;
          }
          if (f.filterType === FILTER_TYPE.SKILL_BUFF_CASTER) {
            exist =
              item.buffInfo?.casterAttributes[f.attribute.id] >= +f.value ||
              item.buffInfo?.targetAttributes[f.attribute.id] >= +f.value;
          }
          if (f.filterType === FILTER_TYPE.IMPACT) {
            exist = item.consumableInfo?.attributes[f.attribute.id] >= +f.value;
            // also check stats
            if (!exist) {
              if (+f.attribute.id === ATTRIBUTES.LIFE) {
                exist = (item.consumableInfo?.buffStats?.life ?? 0) >= +f.value;
              }
              if (+f.attribute.id === ATTRIBUTES.MANA) {
                exist = (item.consumableInfo?.buffStats?.mana ?? 0) >= +f.value;
              }
              if (+f.attribute.id === ATTRIBUTES.LIFE_CHANCES) {
                exist = (item.consumableInfo?.buffStats?.lifeChances ?? 0) >= +f.value;
              }
            }
          }
          if (f.filterType === FILTER_TYPE.MAGIC_ATTACK) {
            if (f.attribute.id === 'manaConsumption') {
              exist = (item.meta?.manaCost ?? 0) >= +f.value;
            }
            if (f.attribute.id === 'minDmg') {
              exist = (item.magicAttackInfo?.minDmg ?? 0) >= +f.value;
            }
            if (f.attribute.id === 'maxDmg') {
              exist = (item.magicAttackInfo?.maxDmg ?? 0) >= +f.value;
            }
            if (f.attribute.id === 'attributesFactorDexterity') {
              exist = (item.magicAttackInfo?.attributesFactor.dexterity ?? 0) >= +f.value;
            }
            if (f.attribute.id === 'attributesFactorEnergy') {
              exist = (item.magicAttackInfo?.attributesFactor.energy ?? 0) >= +f.value;
            }
            if (f.attribute.id === 'attributesFactorStrength') {
              exist = (item.magicAttackInfo?.attributesFactor.strength ?? 0) >= +f.value;
            }
            if (f.attribute.id === 'attributesFactorVitality') {
              exist = (item.magicAttackInfo?.attributesFactor.vitality ?? 0) >= +f.value;
            }
          }
          if (f.filterType === FILTER_TYPE.REQUIRED) {
            if (+f.attribute.id === ATTRIBUTES.STRENGTH) {
              if (f.valueType === 'max') {
                exist = (item.meta?.requirements.strength ?? 0) <= +f.value;
              } else {
                exist = (item.meta?.requirements.strength ?? 0) >= +f.value;
              }
            }
            if (+f.attribute.id === ATTRIBUTES.DEXTERITY) {
              if (f.valueType === 'max') {
                exist = (item.meta?.requirements.dexterity ?? 0) <= +f.value;
              } else {
                exist = (item.meta?.requirements.dexterity ?? 0) >= +f.value;
              }
            }
            if (+f.attribute.id === ATTRIBUTES.VITALITY) {
              if (f.valueType === 'max') {
                exist = (item.meta?.requirements.vitality ?? 0) <= +f.value;
              } else {
                exist = (item.meta?.requirements.vitality ?? 0) >= +f.value;
              }
            }
            if (+f.attribute.id === ATTRIBUTES.ENERGY) {
              if (f.valueType === 'max') {
                exist = (item.meta?.requirements.energy ?? 0) <= +f.value;
              } else {
                exist = (item.meta?.requirements.energy ?? 0) >= +f.value;
              }
            }
          }

          if (!exist) {
            if (f.and === CHECKBOX_STATE.CHECKED) {
              result = false;
              break;
            }
          } else if (f.and !== CHECKBOX_STATE.CHECKED) {
            atLeastOneOrExist = true;
          }
        }
        if (!atLeastOneOrExist) {
          return false;
        }
        return result;
      });
    }

    if (generalFilters) {
      const keys = Object.keys(generalFilters);

      items = items.filter(item => {
        return keys.every(key => {
          let exist = false;
          let value = 0;

          if (key === 'fragility') {
            value = +item[key] / 1000;
          } else {
            value = +resolveValueByPath(item, key);
          }

          exist =
            value >= +generalFilters![key].min && (!generalFilters![key].max || value <= +generalFilters![key].max);

          return exist;
        });
      });
    }

    if (isCollapseItems) {
      const count = countItems(items);

      items = items.filter(it => !it.meta.isConsumableItem && !isOtherItem(it.meta));

      Object.keys(count).forEach(key => {
        const found = originalItems.find(it => it.meta.name === key);
        if (found) {
          items = [
            ...items,
            { ...found, count: count[key], countFormatted: count[key] > 99 ? '99+' : String(count[key]) },
          ];
        }
      });
    }

    return items.filter(it => {
      return onlyConcreteItemType === -1 || it.meta.itemType === onlyConcreteItemType;
    });
  }
}
